function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode?.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        summaryElement.classList.add('mobile-menu-open');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    summaryElement.classList.add('mobile-menu-open');
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    detailsElement.querySelector('summary').classList.remove('mobile-menu-open');
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.vertical = this.getAttribute('data-orientation') === 'vertical';
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        this.initPages();
      }
    });
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => 
      this.vertical ? element.clientHeight > 0 : element.clientWidth > 0
    );
  
    if (this.sliderItemsToShow.length < 2) return;
  
    this.sliderItemOffset = this.vertical 
      ? this.sliderItemsToShow[1]?.offsetTop - this.sliderItemsToShow[0]?.offsetTop || 1
      : this.sliderItemsToShow[1]?.offsetLeft - this.sliderItemsToShow[0]?.offsetLeft || 1;
  
    this.slidesPerPage = Math.max(1, Math.floor(
      this.vertical 
        ? (this.slider.clientHeight - this.sliderItemsToShow[0]?.offsetTop) / this.sliderItemOffset
        : (this.slider.clientWidth - this.sliderItemsToShow[0]?.offsetLeft) / this.sliderItemOffset
    ));
  
    this.totalPages = Math.max(1, Math.ceil(this.sliderItemsToShow.length / this.slidesPerPage));
  
    if (this.totalPages <= 1) {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
    } else {
      if(this.prevButton){
        this.prevButton.style.display = 'block';
      }
      if(this.nextButton){
        this.nextButton.style.display = 'block';
      }
      
    }
  
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;
  
    const previousPage = this.currentPage;
    const scrollPosition = this.vertical ? this.slider.scrollTop : this.slider.scrollLeft;
  
    this.currentPage = Math.round(scrollPosition / this.sliderItemOffset) + 1;
  
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }
  
    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }
  
    if (this.enableSliderLooping) return;
  
    // **Ensure correct detection of last slide**
    const maxScroll = this.vertical
      ? this.slider.scrollHeight - this.slider.clientHeight
      : this.slider.scrollWidth - this.slider.clientWidth;
  
    const isAtStart = this.vertical
      ? this.slider.scrollTop <= 0
      : this.slider.scrollLeft <= 0;
  
    // **Fix: Use Math.abs to prevent rounding issues**
    const isAtEnd = Math.abs(scrollPosition - maxScroll) < 1;
  
    // **Set button states only if they actually change**
    if (isAtStart) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }
  
    // **Extra Condition: Disable next button if currentPage == totalPages**
    if (isAtEnd || this.currentPage === this.totalPages) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    if (this.vertical) {
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return element.offsetTop + element.clientHeight <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
    } else {
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = this.vertical
      ? event.currentTarget.name === 'next'
        ? this.slider.scrollTop + step * this.sliderItemOffset
        : this.slider.scrollTop - step * this.sliderItemOffset
      : event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    if (this.vertical) {
      this.slider.scrollTo({
        top: position,
      });
    } else {
      this.slider.scrollTo({
        left: position,
      });
    }
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    // this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
      
      // **Reset slider whenever variant changes**
      this.resetSlider();
    });

    // Initialize SKU on page load
    this.updateSku();
    this.getVariantDetails();
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  updateSku() {
    // Get the hidden variant metafield value
    const hiddenDimensionElement = this.querySelector('#hidden-variant-dimensions');
    const dynamicDimensionElement = document.querySelector('.dynamic-dimensions');
    
    const hiddenVariantUseElement = this.querySelector('#hidden-variant_use');
    const dynamicVariantUseElement = document.querySelector('.dynamic-variant-use'); 

    const hiddenVariantSKUElement = this.querySelector('#hidden-variant-sku');
    const dynamicVariantSKUElement = document.querySelector('.dynamic-variant-sku');

    const hiddenVariantQTYElement = this.querySelector('#hidden-variant_qty_in_pkg');
    const dynamicVariantQTYElement = document.querySelector('.dynamic-variant-qty-in-pkg');

    // Update the dynamic variant metafield display
    if (hiddenDimensionElement && dynamicDimensionElement) {
      // Clear previous dimension content
      dynamicDimensionElement.innerHTML = '';
      
      const dimensionValue = hiddenDimensionElement.textContent.trim();
      if(dimensionValue) {
        dynamicDimensionElement.innerHTML = `<span><b>Product Dimensions:</b> ${dimensionValue}</span>`;
      }
    }

    // Update the dynamic variant Single use display
    if (hiddenVariantUseElement && dynamicVariantUseElement) {
      // Clear previous dimension content
      dynamicVariantUseElement.innerHTML = '';
      
      const variantUseValue = hiddenVariantUseElement.textContent.trim();
      if(variantUseValue) {
        dynamicVariantUseElement.innerHTML = `<span><b>${variantUseValue}</b></span>`;
      }
    }

    // Update the dynamic variant sku display
    if (hiddenVariantSKUElement && dynamicVariantSKUElement) {
      // Clear previous dimension content
      dynamicVariantSKUElement.innerHTML = '';

      const variantUseValue = hiddenVariantSKUElement.textContent.trim();
      if(variantUseValue) {
        dynamicVariantSKUElement.innerHTML = `<span><b>Former Item #:</b> ${variantUseValue}</span>`;
      }
    }

    // Update the dynamic qty in package display
    if (hiddenVariantQTYElement && dynamicVariantQTYElement) {
      // Clear previous dimension content
      dynamicVariantQTYElement.innerHTML = '';

      const qtyInUse = hiddenVariantQTYElement.textContent.trim();
      if(qtyInUse) {
        dynamicVariantQTYElement.textContent = qtyInUse;
      }
    }
  }
 
  getVariantDetails() {
    const accessoriesContainer = document.getElementById('accessories-container');
    const selectedVariantId = document.getElementById("hidden-variant-id").textContent;
    const productTabAccessoryContent = document.querySelector('.product-tabs__accessory-content');
    const productTabAccessoryLink= document.querySelector('.product-tabs__accessory-link');
    const productTabAccessorySliderButton= document.querySelector('.product-tabs__accessory-content .slider__btn--next');
    const accessoriesAccordionHeader = document.querySelector('.product-tabs__content-wrapper .accordion-header');
    const accessories_content2 = document.getElementById('content_2');
    
    const selectedVariant = window.product?.variants?.find(variant => variant.id === Number(selectedVariantId));

    if (accessoriesContainer) {
      accessoriesContainer.innerHTML = ''; // Clear the current content
    }

    if (selectedVariant && selectedVariant.accessoryMetafields) {
      const accessories = selectedVariant.accessoryMetafields;
      if (accessories && accessories.length > 0) {
        // Show the accessory content
        if (productTabAccessoryContent) {
          if (window.innerWidth > 750) {
            productTabAccessoryContent.style.display = 'flex';
          } else {
            productTabAccessoryContent.style.display = 'flex';
          }
        }
        
        if (productTabAccessoryLink) {
          productTabAccessoryLink.style.display = 'block';
          productTabAccessoryLink.classList.add('active');
          productTabAccessoryLink.nextElementSibling.classList.remove('active');
          accessoriesAccordionHeader?.classList.remove('hidden');
  
          accessories.forEach(accessory => {
            const productIcons = window.productMessagingIcons;
            let productMsgIcons = ``;
            if(accessory.has_freight_quote == "true"){
              productMsgIcons = productIcons.freight_icon;
            }
            if(accessory.has_volume_pricing != ""){
              productMsgIcons += productIcons.volume_pricing_icon;
            }
            if(accessory.fsa_eligible == "Yes"){
              productMsgIcons += productIcons.fsa_icon;
            }
            if(accessory.has_oversize_charge_group != ""){
              productMsgIcons += productIcons.oversize_shipping_icon;
            }
            if(accessory.is_no_end_user_sales == "true"){
              productMsgIcons += productIcons.no_end_user_sales_icon;
            }

            const listItem = document.createElement('li');
            listItem.classList.add('product-tabs__accessories-item');
            listItem.innerHTML = `
              <a href="${accessory.url}">
                <img src="${accessory.variantDetails.featured_image ? accessory.variantDetails.featured_image.src : accessory.productImage }" alt="${accessory.title}" />
                <div class="accessories_card-contain">
                  <span class="accessory-title">${accessory.variantDetails.name}</span>
                  <div class="product_message-wrapper flex flex-item-row flex-center accessory-item">` +
                    productMsgIcons +
                  `</div>
                  <span class="accessory-price">${accessory.price} USD</span>
                </div>
              </a>
              <product-form data-section-id="template--2__sticky_navbar_E">
                <form method="post" action="/cart/add" id="quick-add-template--2_sticky_navbar_E" accept-charset="UTF-8" class="form accessories-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
                  <input type="hidden" name="form_type" value="product">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="id" value="${accessory.id}" class="product-variant-id">
                    <button id="quick-add-template--2__sticky_navbar_E-submit" type="submit" name="add" class="quick-add__submit button button--full-width ${accessory.add_to_cart_button}" aria-haspopup="dialog" aria-labelledby="quick-add-template--22730264609024__sticky_navbar_E4RYxB46930093965568-submit title-template--22730264609024__sticky_navbar_E4RYxB-46930093965568" aria-live="polite" data-sold-out-message="true">
                    <span>
                      Add to cart
                    </span>

                    <div class="loading__spinner hidden">
                      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                      </svg>
                    </div>
                  </button>
                  <input type="hidden" name="product-id" value="${window.product.id}">
                  <input type="hidden" name="section-id" value="template--2__sticky_navbar_E">
                </form>
              </product-form>
            `;

            if(productTabAccessoryContent.classList.contains('hasAccessory')) {
               accessoriesContainer.removeChild(listItem);
            } else {
              accessoriesContainer.appendChild(listItem);
            }

            accessoriesContainer.appendChild(listItem);
            productTabAccessorySliderButton.style.display = 'block';
          });
          productTabAccessoryContent.classList.remove('hidden');
          productTabAccessoryLink.classList.remove('hidden');
        }

        if(accessories && accessories.length <= 4) {
          productTabAccessorySliderButton.style.display = 'none';
        }
        
      } else {
        // Hide the accessory content if no accessories
        if (productTabAccessoryContent) {
          //productTabAccessoryContent.style.display = 'none';
           productTabAccessoryContent.classList.add('hidden');
        }
        if (productTabAccessoryLink) {
          productTabAccessoryLink.style.display = 'none';
          productTabAccessoryLink.classList.remove('active');
          productTabAccessoryLink.nextElementSibling.classList.add('active');
          accessoriesAccordionHeader?.classList.add('hidden');
         // accessories_content2.style.display = 'block';
        }
      }
    } else {
      accessoriesAccordionHeader.classList.add('hidden');
      //accessories_content2.style.display = 'block';
       productTabAccessoryLink?.nextElementSibling?.classList.add('active');
      // Hide the accessory content if no variant data
      if (productTabAccessoryContent) {
        productTabAccessoryContent.classList.add('hidden');
        productTabAccessoryLink.classList.add('hidden');
        //accessories_content2.style.display = 'block';
      }
    }

    const return_policy_link_on_pdp = document.getElementById('product_desc-return-policy');
    if (selectedVariant && selectedVariant.returnable) {
      return_policy_link_on_pdp?.classList.remove('visually-hidden');
    } else {
      return_policy_link_on_pdp?.classList.add('visually-hidden');
    }
    
    setTimeout(() => {
      if(selectedVariantId) {
        this.showIconsOnProductQuickViewModal(selectedVariantId);
      }
      
    }, 500);
  }

  showIconsOnProductQuickViewModal(variantId) {
    let variantDataForIconsOnQuickViewModal = false;

    if(window.productVariants){
       variantDataForIconsOnQuickViewModal = window.productVariants[variantId] || false;
    }
    
    if (!variantDataForIconsOnQuickViewModal) {
      console.error('Variant data not found for ID:', variantId);
      return;
    }

    const variantFreightTruckValueQv = variantDataForIconsOnQuickViewModal.variantFreightTruck || '';
    const variantChargeGroupValueQv = variantDataForIconsOnQuickViewModal.variantChargeGroup ? variantDataForIconsOnQuickViewModal.variantChargeGroup : '';
    const variantFsaEligibleValueQv = variantDataForIconsOnQuickViewModal.variantFsaEligible || '';
    const variantVolumePricingValueQv = variantDataForIconsOnQuickViewModal.volumePricing?.length > 0 ? true : false;
    const variantNoEndUsersSalesValueQv = variantDataForIconsOnQuickViewModal.variantNoEndUserSales || '';
    const variantSkuStatus = variantDataForIconsOnQuickViewModal.variantSkuStatus || ''; 
    const variantInventoryQty = variantDataForIconsOnQuickViewModal.variantInventoryQty || ''; 
    const customer_group_noenduser = variantDataForIconsOnQuickViewModal.customer_group_noenduser || false;
    this.updateVariantAttributesForCheckoutMessageQv(variantChargeGroupValueQv, variantFreightTruckValueQv, variantFsaEligibleValueQv, variantVolumePricingValueQv, variantNoEndUsersSalesValueQv, variantSkuStatus, variantInventoryQty, customer_group_noenduser);
  }

  updateVariantAttributesForCheckoutMessageQv(newChargeGroupValue, newFreigthTruckValue, newFsaEligibleValue, newVolumePricingValue, newNoEndUserSalesValue, variantSkuStatus, variantInventoryQty, customer_group_noenduser) {
    const chargeGroupElement = document.getElementById('variant_charge-group');
    const freigthTruckElement = document.getElementById('variant_freight-truck');
    chargeGroupElement.value = newChargeGroupValue;
    freigthTruckElement.value = newFreigthTruckValue;

    const freigthTruckIcon = document.getElementById('freight_icon-ctc');
    if(newFreigthTruckValue == true) {
      freigthTruckIcon.classList.remove('hidden'); 
    } else {
     freigthTruckIcon?.classList.add('hidden'); 
    }
    
    const oversizedShippingIcon = document.getElementById('oversizedShipping_icon-ctc');
    if(newChargeGroupValue != '' ) {
      oversizedShippingIcon.classList.remove('hidden'); 
    } else {
     oversizedShippingIcon?.classList.add('hidden'); 
    }

    const fsaEligibleIcon = document.getElementById('fsaEligible_icon-ctc');
    if(newFsaEligibleValue == "Yes") {
      fsaEligibleIcon.classList.remove('hidden'); 
    } else {
      fsaEligibleIcon?.classList.add('hidden'); 
    }

    const volumePricingIcon = document.getElementById('volumePricing_icon-ctc');
    const volumePricingMessage = document.querySelector('.pdp_mobile-volume_pricing');

    const quickViewModalPdpFormQuantity = document.querySelector('.quick-add-modal .product-form__quantity');
    const parentNodeProductInfoContainer = document.querySelector('.quick-add-modal .product__info-container');

    if(newVolumePricingValue == true) {
      volumePricingIcon.classList.remove('hidden'); 
      if(volumePricingMessage) {
        volumePricingMessage.style.display = "block";
      }
      if (window.innerWidth <= 750) { // Adjust breakpoint as needed for "mobile"
        if(parentNodeProductInfoContainer) {
          volumePricingMessage.classList.add('variantMesg');
         parentNodeProductInfoContainer.insertBefore(volumePricingMessage, quickViewModalPdpFormQuantity);
        }
      } 
    } else {
      volumePricingIcon?.classList.add('hidden'); 
    }

    const noEndUsersIcon = document.getElementById('endusersales_icon-ctc');
    const formRegAddToCartBtnName = document.getElementById('addToCartBtnName');
    const formNoEndUsersSalesBtnName = document.getElementById('noEndUsersSalesBtnName');
    const formSbBtn = formRegAddToCartBtnName.parentElement;
    const customer_group_message = document.querySelector('.product .customer_grp_msg');

    if(newNoEndUserSalesValue == true) {
      noEndUsersIcon.classList.remove('hidden'); 
      formNoEndUsersSalesBtnName.classList.remove('hidden'); 
      formRegAddToCartBtnName.classList.add('hidden'); 
      formSbBtn?.classList.add('disabled');
      if(customer_group_message) {
        customer_group_message.innerHTML = "Contact Us";
        formSbBtn?.classList.add('disable_contact_us');
      }
      if(((variantSkuStatus == "DC" || variantSkuStatus == "DC, Sell Inventory") && variantInventoryQty > 0) && !customer_group_noenduser) {
        formNoEndUsersSalesBtnName?.classList.add('hidden'); 
        formRegAddToCartBtnName?.classList.remove('hidden'); 
      } 
    } else {
      noEndUsersIcon.classList.add('hidden'); 
      formNoEndUsersSalesBtnName?.classList.add('hidden'); 
      formRegAddToCartBtnName?.classList.remove('hidden'); 
      
      formSbBtn?.classList.remove('disabled'); 
      if((variantSkuStatus == "DC" || variantSkuStatus == "DC, Sell Inventory") && variantInventoryQty > 0) {
        formNoEndUsersSalesBtnName?.classList.add('hidden'); 
        formRegAddToCartBtnName?.classList.remove('hidden'); 
      }
    }
  }

  resetSlider() {
    const sliderComponent = document.querySelector('slider-component');
    if (sliderComponent) {
      sliderComponent.resetPages();
    }
  }
  
  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

// Highlight Resource and Support Title as per URL
document.addEventListener("DOMContentLoaded", function() {
  let currentResourcePageUrl = window.location.href;
  if (currentResourcePageUrl.includes("blogs")) {
    let resourceLinks = document.querySelectorAll(".resource__title-link");
    resourceLinks.forEach(function(link) {
      link.classList.remove("resource-selected")
    }),
    resourceLinks.forEach(function(link) {
      let linkUrl = link.getAttribute("href");
      currentResourcePageUrl.includes(linkUrl) && link.classList.add("resource-selected")
    })
  }
});

// Highlight Blog Categories as per URL
document.addEventListener("DOMContentLoaded", function() {
  let currentPageUrl = window.location.href;
  if (currentPageUrl.includes("blogs")) {
    let categoryLinks = document.querySelectorAll(".blog__category-link");
    categoryLinks.forEach(function(link) {
      link.classList.remove("category-selected")
    }),
    categoryLinks.forEach(function(link) {
      let linkUrl = link.getAttribute("href");
      currentPageUrl.includes(linkUrl) && link.classList.add("category-selected")
    })
  }
});

document.addEventListener("DOMContentLoaded", function() {
  const titleElement = document.querySelector(".article-template__title");
  if (titleElement) {
    titleElement.innerHTML = titleElement.innerHTML.replace(/(®)/g, '<span style="font-size: 20px; padding: 0 5px; position: relative; top: -10px;">$1</span>');
  }
});

// Header Navigation 
document.addEventListener('DOMContentLoaded', () => {
  const menuNavigation = document.querySelector('.desktop-menu-drawer__navigation');
  const menuItems = document.querySelectorAll('.shop_header__menu-item');
  const menuDrawers = document.querySelectorAll('.menu-drawer__wrapper');
  const overlay = document.querySelector('.overlay');
  const closeButtons = document.querySelectorAll('.menu-drawer__close-btn');
  const menuItemLinks = document.querySelectorAll('.desktop-menu-drawer__menu-item-link');
  
  function openDrawer(targetId) {
    menuDrawers.forEach(drawer => {
      if (drawer.id === targetId) {
        drawer.classList.add('menu-open');
      } else {
        drawer.classList.remove('menu-open');
      }
    });
    overlay.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    document.querySelectorAll('.menu-drawer__grandchild-menu').forEach(menu => {
      menu.style.display = 'none';
    });
    menuDrawers.forEach(drawer => {
      drawer.classList.remove('menu-open');
    });
    overlay.classList.remove('visible');
    document.body.style.overflow = 'auto';

    // Remove the "menu-activated" class from all links
    menuItemLinks.forEach(link => {
      link.classList.remove("menu-activated");
    });
  }

  // Initialize menu on DOMContentLoaded
  function initializeMenu() {
    menuItems.forEach(item => {
      item.addEventListener('click', () => {
        const targetId = item.id;
        openDrawer(targetId);
      });
    });

    closeButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        closeDrawer();
      });
    });

    overlay.addEventListener('click', () => {
      closeDrawer();
    });

    menuItemLinks.forEach(link => {
      link.addEventListener('click', (event) => {
        menuItemLinks.forEach(item => item.classList.remove("menu-activated"));
        link.classList.toggle("menu-activated");
        toggleGrandchildMenu(event);
      });
    });
  }

  function toggleGrandchildMenu(event) {
    const target = event.currentTarget;
    const grandchildMenuId = target.getAttribute('id').replace('HeaderDrawer-', 'childlink-');
    const grandchildMenu = document.getElementById(grandchildMenuId);
    
    if (grandchildMenu) {
      const isVisible = grandchildMenu.style.display === 'block';

      // Hide all grandchild menus
      document.querySelectorAll('.menu-drawer__grandchild-menu').forEach(menu => {
        menu.style.display = 'none';
      });

      // Show the clicked grandchild menu if it was not visible
      if (!isVisible) {
        grandchildMenu.style.display = 'block';
      }
    }
  }
  window.onload = function() {
    menuNavigation.style.display = 'block';
  };
  initializeMenu();

  // Mobile Navigation
  let mobileMenuDrawer = document.querySelector('.menu-drawer__static-navigation')
  let shopMenu = document.getElementById('mobile-shop__menu-drawer');
  let firstLevelCategory = document.querySelector('.top__menu-drawer');
  let shopCloseButton = document.querySelector('.menu-drawer__shop-close-button');

  shopMenu.addEventListener('click', () => {
    firstLevelCategory.classList.add('top-menu-open');
    mobileMenuDrawer.classList.add('shop-menu-open');
  });

  shopCloseButton.addEventListener('click', () => {
    firstLevelCategory.classList.remove('top-menu-open');
    mobileMenuDrawer.classList.remove('shop-menu-open');
  });
});

// Function to setup collection click handlers
function setupCollectionClickHandlers() {
  const collectionItems = document.querySelectorAll('.desktop-menu-drawer__grandchild-menu li');

  collectionItems.forEach(item => {
    item.addEventListener('click', (event) => {
      
      // Get the collection and main title
      const collectionName = item.getAttribute('data-collection');
      const mainTitle = item.getAttribute('data-main-title');

      // Replace spaces and '&' with '-' and remove extra dashes
      const formattedCollectionName = collectionName.toLowerCase()
        .replace(/,/g, '')  // Replace '&' with '-'
        .replace(/&/g, '-')  // Replace '&' with '-'
        .replace(/\s+/g, '-') // Replace one or more spaces with '-'
        .replace(/--+/g, '-') // Replace consecutive dashes with a single dash
        .replace(/^-|-$/g, ''); // Remove leading or trailing dashes

      const collectionUrl = `/collections/${formattedCollectionName}` +
          `?mainTitle=${encodeURIComponent(mainTitle)}&collectionName=${encodeURIComponent(collectionName)}`;

      // Navigate to the collection page while preserving query parameters
      // history.pushState(null, null, collectionUrl);
      // window.location.reload();

      // The code above was causing the Back button to malfunction, but keeping it there in case replacing it breaks something else
      window.location.href = collectionUrl;
    });
  });
}

// Function to update the breadcrumb
function updateBreadcrumb(mainTitle, collectionName) {
  const breadcrumb = document.getElementById('breadcrumb');
  if (breadcrumb) {

  // Replace spaces and '&' with '-' and remove extra dashes
      const formattedMainTitleName = mainTitle.toLowerCase()
        .replace(/,/g, '')  // Replace '&' with '-'
        .replace(/&/g, '-')  // Replace '&' with '-'
        .replace(/\s+/g, '-') // Replace one or more spaces with '-'
        .replace(/--+/g, '-') // Replace consecutive dashes with a single dash
        .replace(/^-|-$/g, ''); // Remove leading or trailing dashes

    breadcrumb.innerHTML = `<a href="/"> Home </a> / <a href="/collections/${formattedMainTitleName}">${mainTitle}</span> / <span>${collectionName}</span>`;
  }
}

// Function to get URL parameters
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    mainTitle: params.get('mainTitle'),
    collectionName: params.get('collectionName'),
  };
}

// Function to initialize the breadcrumb on page load
function initializeBreadcrumb() {
  const breadcrumb = document.getElementById('breadcrumb');
  const { mainTitle, collectionName } = getUrlParams();

  if (mainTitle && collectionName) {
    updateBreadcrumb(mainTitle, collectionName);
  }
}

// Function to update anchor tags with URL parameters
function updateAnchorTags() {
  const anchorTags = document.querySelectorAll('a.get-breacrumb-details');
  const { mainTitle, collectionName } = getUrlParams();

  anchorTags.forEach(anchor => {
    if (mainTitle && collectionName) {
      // Construct the new URL with query parameters
      const updatedUrl = `${anchor.href}?mainTitle=${encodeURIComponent(mainTitle)}&collectionName=${encodeURIComponent(collectionName)}`;
      anchor.href = updatedUrl;
    }
  });
}

// Function to update the breadcrumb in the collection page
function updateCollectionBreadcrumb() {
  const { mainTitle, collectionName } = getUrlParams();
  const mainCollectionTitle = document.getElementById('main-collection-title');
  const subCollectionTitle = document.getElementById('sub-collection-title');
  const presentCollectionTitle = document.getElementById('collection-title')?.textContent;

  if (mainTitle && mainCollectionTitle) {
    mainCollectionTitle.textContent = `${mainTitle} / `; // Set the main title

    // Replace spaces and '&' with '-' and remove extra dashes
    const formattedMainTitleName = mainTitle.toLowerCase()
      .replace(/&/g, '-')   // Replace '&' with '-'
      .replace(/,/g, '-')   // Replace ',' with '-'
      .replace(/\s+/g, '-') // Replace one or more spaces with '-'
      .replace(/--+/g, '-') // Replace consecutive dashes with a single dash
      .replace(/^-|-$/g, ''); // Remove leading or trailing dashes

    mainCollectionTitle.href = `/collections/${formattedMainTitleName}`; 
  }

  if (collectionName && subCollectionTitle) {
    subCollectionTitle.textContent = `${collectionName} / `; // Set the collection name
    const subCollectionTitleValue = `${collectionName}`;

    // Replace spaces and '&' with '-' and remove extra dashes
    const formattedCollectionName = collectionName.toLowerCase()
      .replace(/&/g, '-')   // Replace '&' with '-'
      .replace(/,/g, '-')   // Replace ',' with '-'
      .replace(/\s+/g, '-') // Replace one or more spaces with '-'
      .replace(/--+/g, '-') // Replace consecutive dashes with a single dash
      .replace(/^-|-$/g, ''); // Remove leading or trailing dashes

    if(subCollectionTitleValue != presentCollectionTitle) {
      subCollectionTitle.href = `/collections/${formattedCollectionName}` +
      `?mainTitle=${encodeURIComponent(mainTitle)}&collectionName=${encodeURIComponent(collectionName)}`; // Update the href with query params
    } else {
      subCollectionTitle.textContent = '';
    }
  }
}

// Initialize everything after DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  setupCollectionClickHandlers();
  initializeBreadcrumb();
  updateAnchorTags();
  updateCollectionBreadcrumb();

  const hiddenVariantID = document.querySelector("#hidden-variant-id");
  if(hiddenVariantID) {
    const activeThumbnail2 = window.variantMetafields[hiddenVariantID.innerText].variant_image_2_filename;
  }

  setTimeout(() => {
    const privacyPolicyLink = document.getElementById('shopify-pc__banner__body-policy-link');
    privacyPolicyLink?.setAttribute('aria-label', 'Visit Privacy Policy (opens in a new tab)');
  }, 300);
});

// (function() {
//   document.addEventListener("click", function(event) {
//       let clickedElement = event.target.closest("a");

//       if (clickedElement) {
//           // Get current domain dynamically 
//           let currentDomain = window.location.origin; 
//           let targetUrl = new URL(clickedElement.href);

//           // Ensure redirection happens only within the same domain and is a product page
//           if (targetUrl.origin === currentDomain && targetUrl.pathname.includes("/products/")) {
//               event.preventDefault(); // Block the default redirection

//               // Get the current URL query parameters
//               let currentParams = window.location.search;

//               // Append the extracted parameters to the target URL
//               targetUrl.search = currentParams;

//               // Redirect to the modified URL
//               window.location.href = targetUrl.href;
//           }
//       }
//   });
// })();

// Klevu Customization to hide facets depending on page and make mobile facets collapsed by default
window._klvReady = window._klvReady || [];
window._klvReady.push(function () {
  klevu.afterTemplateRender('landing,catnav', function(data, scope){
    collapseMobileFacets();
    hideSelectFacets();
  } )
});
      
function collapseMobileFacets() {
  // This variable ensures we only collapse the filters on page load
  let klevu_filters_collapsed = false;
  
  // Change this value to true if you only want to collapse filters on mobile
  let klevu_filters_collapsed_mobile_only = true;

  if (!klevu_filters_collapsed && (klevu.dom.helpers.isMobileDevice() || !klevu_filters_collapsed_mobile_only)) {
    // Find all filter triggers and click them to collapse them
    var filterHeaders = document.querySelectorAll('.klevuMeta[data-section="productList"] .kuFilterHead.kuCollapse');
    if (filterHeaders.length) {
        klevu.each(filterHeaders, function(key, header){
            header.click();
        });
    }          
    klevu_filters_collapsed = true;
  } 
}

function hideSelectFacets() {
    // Select all kuFilterBox elements except Product Category, Price, Brand, CE Mark, and TAA Compliant  within the target parent
    const filterBoxes = document.querySelectorAll('.klevuLanding.klevuTarget[id^="klevuLanding_searchResults"] .kuFilterBox:not([data-filter="category"]):not([data-filter="klevu_price"]):not([data-filter="brand"]):not([data-filter="ce_mark"]):not([data-filter="taa_compliant"])');

    // Loop through each filter box and apply style
    filterBoxes.forEach(function(box) {
        box.style.setProperty('display', 'none', 'important');
    });
}

// Klevu Customization: hide "Add to compare" on non-product results
window._klvReady.push(function () {
  klevu.afterTemplateRender('all', function(data, scope){
    // Observe the document body for changes
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          const addToCompareEls = document.querySelectorAll(".contentList sc-add-to-compare");
          addToCompareEls.forEach(el => {
            // Get shadow root
            const shadowRoot = el.shadowRoot;
            shadowRoot.querySelector(".checkbox-control").style.setProperty('display', 'none', 'important');
          })
        }
      });
    });

    // Start observing the body for added child elements
    observer.observe(document.body, { childList: true, subtree: true });
  });
});

// Klevu Search Result Customization : To seacrh by variant option and SKU
window._klvReady = window._klvReady || [];
window._klvReady.push(function () {
  klevu.modifyResponse("all", function (data, scope) {
    function replaceItemURL(index, item) {
      let newURL = null;
  
      if (window.isURLStoreCodeRequired) {
        newURL = item.url.replace(window.baseURLRegex, window.origin + "/");
      } else {
        newURL = item.url + "?variant=" + item.id;
      }
  
      item.url = newURL;
    }
  
    Object.entries(data.template.query).map(([resultType, resultTypeObject]) => {
      if (resultTypeObject.result) {
        klevu.each(resultTypeObject.result, replaceItemURL);
      }
    });
  });
});

// Klevu Customization: Pagination
window._klvReady = window._klvReady || [];
window._klvReady.push(function () {
    const klevuLandingTemplatePaginationCustom = `
    <% if(data.query[dataLocal].result.length > 0 ) { %>
        <% var productListLimit = data.query[dataLocal].meta.noOfResults; %>
        <% var productListTotal = data.query[dataLocal].meta.totalResultsFound - 1; %>
        <div class="kuPagination">
            <% if(data.query[dataLocal].meta.offset > 0) { %>
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="0">&lt;&lt;</a>&nbsp;
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="<%=(data.query[dataLocal].meta.offset-productListLimit)%>">&lt;</a>&nbsp;
            <% } %>
            <% if(data.query[dataLocal].meta.offset > 0) { %>
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="<%=(data.query[dataLocal].meta.offset-productListLimit)%>"><%=(Math.ceil(data.query[dataLocal].meta.offset/productListLimit))%></a>&nbsp;
            <% } %>
            <a href="javascript:void(0);" class="kuCurrent"><%=(Math.ceil(data.query[dataLocal].meta.offset/productListLimit)+1)%></a>&nbsp;
            <% if(productListTotal >= data.query[dataLocal].meta.offset+productListLimit) { %>
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="<%=(data.query[dataLocal].meta.offset+productListLimit)%>"><%=(Math.ceil(data.query[dataLocal].meta.offset/productListLimit)+2)%></a>&nbsp;
            <% } %>
            <% if(productListTotal >= data.query[dataLocal].meta.offset+(productListLimit*2)) { %>
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="<%=(data.query[dataLocal].meta.offset + (productListLimit*2))%>"><%=(Math.ceil(data.query[dataLocal].meta.offset/productListLimit)+3)%></a>
            <% } %>
            <% if(productListTotal >= data.query[dataLocal].meta.offset+productListLimit) { %>
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="<%=(data.query[dataLocal].meta.offset+productListLimit)%>">&gt;</a>&nbsp;
                <a target="_self" href="javascript:void(0)" class="klevuPaginate" data-offset="<%=(Math.floor(productListTotal/productListLimit)*productListLimit)%>">&gt;&gt;</a>
            <% } %>
        </div>
    <% } %>`;
    const klevuLandingTemplateResultsCustom = `
    <div class="kuResultsListing">
        <div class="productList klevuMeta" data-section="productList">
            <div class="kuResultContent">
                <div class="kuResultWrap <%=(data.query.productList.filters.length == 0 )?'kuBlockFullwidth':''%>">
                    <div ku-container data-container-id="ku_landing_main_content_container" data-container-role="content">
                        <section ku-container data-container-id="ku_landing_main_content_left" data-container-position="left" data-container-role="left">
                            <div ku-block data-block-id="ku_landing_left_facets">
                                <%=helper.render('filters',scope,data,"productList") %>
                            </div>
                            <div ku-block data-block-id="ku_landing_left_call_outs"></div>
                            <div ku-block data-block-id="ku_landing_left_banner"></div>
                        </section>
                        <section ku-container data-container-id="ku_landing_main_content_center" data-container-position="center" data-container-role="center">
                            <header ku-block data-block-id="ku_landing_result_header">
                                <%=helper.render('klevuLandingTemplateResultsHeadingTitle',scope,data,"productList") %>
                                <%=helper.render('filtersTop',scope,data,"productList") %>
                                <%= helper.render('kuFilterTagsTemplate',scope,data,"productList") %>
                                <% if(helper.hasResults(data,"productList")) { %>
                                    <%=helper.render('sortBy',scope,data,"productList") %>
                                    <%=helper.render('limit',scope,data,"productList") %>
                                    <%=helper.render('kuTemplateLandingResultsViewSwitch',scope,data,"productList") %>
                                <% } %>
                                <div class="kuClearBoth"></div>
                            </header>
                            <div ku-block data-block-id="ku_landing_result_items">
                                <div class="kuResults">
                                    <% if(helper.hasResults(data,"productList")) { %>
                                        <ul>
                                            <% helper.each(data.query.productList.result,function(key,item){ %>
                                                <% if(item.typeOfRecord == "KLEVU_PRODUCT") { %>
                                                    <%=helper.render('productBlock',scope,data,item) %>
                                                <% } %>
                                            <% }); %>
                                        </ul>
                                    <% } else { %>
                                        <div class="kuNoRecordsFoundLabelTextContainer">
                                            <span class="kuNoRecordsFoundLabelText"><%= helper.translate("No records found for your selection") %></span>
                                        </div>
                                    <% } %>
                                    <div class="kuClearBoth"></div>
                                </div>
                                <%=helper.render('pagination',scope,data,"productList") %>
                            </div>
                            <div ku-block data-block-id="ku_landing_other_items">
                                <%=helper.render('klevuLandingTemplateInfiniteScrollDown',scope,data) %>
                            </div>
                            <footer ku-block data-block-id="ku_landing_result_footer"></footer>
                        </section>
                        <section ku-container data-container-id="ku_landing_main_content_right" data-container-position="right" data-container-role="right">
                            <div ku-block data-block-id="ku_landing_right_facets"></div>
                            <div ku-block data-block-id="ku_landing_right_call_outs"></div>
                            <div ku-block data-block-id="ku_landing_right_banner"></div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
        <% if(data.query.contentList) { %>
            <div class="contentList klevuMeta" data-section="contentList" data-result-view="list">
                <div class="kuResultContent">
                    <div class="kuResultWrap <%=(data.query.contentList.filters.length == 0 )?'kuBlockFullwidth':''%>">
                        <div ku-container data-container-id="ku_landing_main_content_container" data-container-role="content">
                            <section ku-container data-container-id="ku_landing_main_content_left" data-container-position="left" data-container-role="left">
                                <div ku-block data-block-id="ku_landing_left_facets">
                                    <%=helper.render('filters',scope,data,"contentList") %>
                                </div>
                                <div ku-block data-block-id="ku_landing_left_call_outs"></div>
                                <div ku-block data-block-id="ku_landing_left_banner"></div>
                            </section>
                            <section ku-container data-container-id="ku_landing_main_content_center" data-container-position="center" data-container-role="center">
                                <header ku-block data-block-id="ku_landing_result_header">
                                    <%=helper.render('filtersTop',scope,data,"contentList") %>
                                    <%= helper.render('kuFilterTagsTemplate',scope,data,"contentList") %>
                                    <% if(helper.hasResults(data,"contentList")) { %>
                                        <%=helper.render('limit',scope,data,"contentList") %>
                                        <%=helper.render('pagination',scope,data,"contentList") %>
                                    <% } %>
                                    <div class="kuClearBoth"></div>
                                </header>
                                <div ku-block data-block-id="ku_landing_result_items">
                                    <div class="kuClearBoth"></div>
                                    <div class="kuResults">
                                        <% if(helper.hasResults(data,"contentList")) { %>
                                            <ul>
                                                <% helper.each(data.query.contentList.result,function(key,item){ %>
                                                    <% if(item.typeOfRecord == "KLEVU_CMS") { %>
                                                        <%=helper.render('contentBlock',scope,data,item) %>
                                                    <% }%>
                                                <% }); %>
                                            </ul>
                                        <% } else { %>
                                            <div class="kuNoRecordsFoundLabelTextContainer">
                                                <span class="kuNoRecordsFoundLabelText"><%= helper.translate("No records found for your selection") %></span>
                                            </div>
                                        <% } %>
                                        <div class="kuClearBoth"></div>
                                    </div>
                                    <%=helper.render('pagination',scope,data,"contentList") %>
                                </div>
                                <div ku-block data-block-id="ku_landing_other_items">
                                    <%=helper.render('klevuLandingTemplateInfiniteScrollDown',scope,data) %>
                                </div>
                                <footer ku-block data-block-id="ku_landing_result_footer"></footer>
                            </section>
                            <section ku-container data-container-id="ku_landing_main_content_right" data-container-position="right" data-container-role="right">
                                <div ku-block data-block-id="ku_landing_right_facets"></div>
                                <div ku-block data-block-id="ku_landing_right_call_outs"></div>
                                <div ku-block data-block-id="ku_landing_right_banner"></div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        <% } %>
    </div>
`;
    klevu.beforeActivation("landing, catnav", function (data, scope) {
        scope.kScope.template.setTemplate(klevuLandingTemplateResultsCustom, "results", true);
        scope.kScope.template.setTemplate(klevuLandingTemplatePaginationCustom, "pagination", true);
    });
});

// Klevu PLP Customization
var klevu_storeTranslations = {
    "Items per page : %s": "Display: %s",
}

window._klvReady = window._klvReady || [];
window._klvReady.push(function () {

const klevuLandingTemplateResultsHeadingTitleCustom = `
    <%
        var totalNumberOfResults = klevu.getObjectPath(data,"query."+dataLocal+".meta.totalResultsFound");
        var searchedTerm = helper.escapeHTML(data.settings.term);
        var isCATNAV = data.settings.isCATNAV;
        if(isCATNAV){
            searchedTerm = helper.escapeHTML(data.settings.categoryPath);
        }
    %>
    <div class="kuResultsHeadingTitleContainer">
        <strong class="kuResultsNumber"> <%= (totalNumberOfResults) ? totalNumberOfResults : 0 %> </strong> <%= helper.translate((parseInt(totalNumberOfResults) > 1) ? " Items" : " Item") %> 
    </div>
`;

const klevuLandingTemplateResults_custom = `
    <div class="kuResultsListing">
        <div class="productList klevuMeta" data-section="productList">
            <div class="kuResultContent">
                <div class="kuResultWrap <%=(data.query.productList.filters.length == 0 )?'kuBlockFullwidth':''%>">

                    <div ku-container data-container-id="ku_landing_main_content_container" data-container-role="content">
                        <section ku-container data-container-id="ku_landing_main_content_left" data-container-position="left" data-container-role="left">
                            <div ku-block data-block-id="ku_landing_left_facets">                                
                                <%=helper.render('filters',scope,data,"productList") %>
                            </div>
                            <div ku-block data-block-id="ku_landing_left_call_outs"></div>
                            <div ku-block data-block-id="ku_landing_left_banner"></div>
                        </section>
                        <section ku-container data-container-id="ku_landing_main_content_center" data-container-position="center" data-container-role="center">
                                <%
                                    var searchedTerm = helper.escapeHTML(data.settings.term);
                                    var isCATNAV = data.settings.isCATNAV;
                                    if(isCATNAV){
                                        searchedTerm = helper.escapeHTML(data.settings.categoryPath);
                                    }
                                %>
                                <h1 class="klevu-title"><%=klevu.dom.helpers.cleanCatogeryPath(searchedTerm)%></h1>
                            <header ku-block data-block-id="ku_landing_result_header">
                                <%=helper.render('klevuLandingTemplateResultsHeadingTitle',scope,data,"productList") %>
                                <%=helper.render('filtersTop',scope,data,"productList") %>
                                <%= helper.render('kuFilterTagsTemplate',scope,data,"productList") %>
                                <% if(helper.hasResults(data,"productList")) { %>
                                    <%=helper.render('sortBy',scope,data,"productList") %>
                                    <%=helper.render('limit',scope,data,"productList") %>
                                    <%=helper.render('kuTemplateLandingResultsViewSwitch',scope,data,"productList") %>
                                <% } %>
                                <div class="kuClearBoth"></div>
                            </header>

                            <div ku-block data-block-id="ku_landing_result_items">
                                <div class="kuResults">
                                    <% if(helper.hasResults(data,"productList")) { %>
                                        <ul>
                                            <% helper.each(data.query.productList.result,function(key,item){ %>
                                                <% if(item.typeOfRecord == "KLEVU_PRODUCT") { %>
                                                    <%=helper.render('productBlock',scope,data,item) %>
                                                <% } %>
                                            <% }); %>
                                        </ul>
                                    <% } else { %>
                                        <div class="kuNoRecordsFoundLabelTextContainer">
                                            <span class="kuNoRecordsFoundLabelText"><%= helper.translate("No records found for your selection") %></span>
                                        </div>
                                    <% } %>
                                    <div class="kuClearBoth"></div>
                                </div>
                                <%=helper.render('pagination',scope,data,"productList") %>
                            </div>
                            
                            <div ku-block data-block-id="ku_landing_other_items">
                                <%=helper.render('klevuLandingTemplateInfiniteScrollDown',scope,data) %>                               
                            </div>
                            <footer ku-block data-block-id="ku_landing_result_footer"></footer>
                        </section>
                        <section ku-container data-container-id="ku_landing_main_content_right" data-container-position="right" data-container-role="right">
                            <div ku-block data-block-id="ku_landing_right_facets"></div>
                            <div ku-block data-block-id="ku_landing_right_call_outs"></div>
                            <div ku-block data-block-id="ku_landing_right_banner"></div>
                        </section>
                    </div>
                    
                </div>
            </div>
        </div>

        <% if(data.query.contentList) { %>
            <div class="contentList klevuMeta" data-section="contentList" data-result-view="list">
                <div class="kuResultContent">
                    <div class="kuResultWrap <%=(data.query.contentList.filters.length == 0 )?'kuBlockFullwidth':''%>">
    
                        <div ku-container data-container-id="ku_landing_main_content_container" data-container-role="content">
                            <section ku-container data-container-id="ku_landing_main_content_left" data-container-position="left" data-container-role="left">
                                <div ku-block data-block-id="ku_landing_left_facets">
                                    
                                    <%=helper.render('filters',scope,data,"contentList") %>
    
                                </div>
                                <div ku-block data-block-id="ku_landing_left_call_outs"></div>
                                <div ku-block data-block-id="ku_landing_left_banner"></div>
                            </section>
                            <section ku-container data-container-id="ku_landing_main_content_center" data-container-position="center" data-container-role="center">
                                
                                <header ku-block data-block-id="ku_landing_result_header">
                                    <%=helper.render('filtersTop',scope,data,"contentList") %>
                                    <%= helper.render('kuFilterTagsTemplate',scope,data,"contentList") %>
                                    <% if(helper.hasResults(data,"contentList")) { %>
                                        <%=helper.render('limit',scope,data,"contentList") %>
                                        <%=helper.render('pagination',scope,data,"contentList") %>
                                    <% } %>
                                    <div class="kuClearBoth"></div> 
                                </header>

                                <div ku-block data-block-id="ku_landing_result_items">
                                    <div class="kuClearBoth"></div>
                                    <div class="kuResults">
                                        <% if(helper.hasResults(data,"contentList")) { %>
                                            <ul>
                                                <% helper.each(data.query.contentList.result,function(key,item){ %>
                                                    <% if(item.typeOfRecord == "KLEVU_CMS") { %>
                                                        <%=helper.render('contentBlock',scope,data,item) %>
                                                    <% }%>
                                                <% }); %>
                                            </ul>
                                        <% } else { %>
                                            <div class="kuNoRecordsFoundLabelTextContainer">
                                                <span class="kuNoRecordsFoundLabelText"><%= helper.translate("No records found for your selection") %></span>
                                            </div>
                                        <% } %>
                                        <div class="kuClearBoth"></div>
                                    </div>
                                    <%=helper.render('pagination',scope,data,"productList") %>
                                </div>
                                <div ku-block data-block-id="ku_landing_other_items">
                                    <%=helper.render('klevuLandingTemplateInfiniteScrollDown',scope,data) %>
                                </div>
                                <footer ku-block data-block-id="ku_landing_result_footer"></footer>
                            </section>
                            <section ku-container data-container-id="ku_landing_main_content_right" data-container-position="right" data-container-role="right">
                                <div ku-block data-block-id="ku_landing_right_facets"></div>
                                <div ku-block data-block-id="ku_landing_right_call_outs"></div>
                                <div ku-block data-block-id="ku_landing_right_banner"></div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        <% } %> 
    </div>
`;
    klevu.beforeActivation("catnav", function (data, scope) {
      scope.kScope.template.setTemplate(klevuLandingTemplateResultsHeadingTitleCustom, "klevuLandingTemplateResultsHeadingTitle", true);
      scope.kScope.template.setTemplate(klevuLandingTemplateResults_custom, "results", true);
    });
})

// Klevu App Search Customization
window._klvReady = window._klvReady || [];
window._klvReady.push(function () {
    klevu.beforeActivation("quick", function (data, scope) {
        klevu.each(klevu.search.extraSearchBox, function (key, box) {
            box.getScope().template.setTemplate(
                klevuQuickTemplateBaseCustom,
                "klevuTemplateBase",
                true
            );
        });
    });

    const klevuQuickTemplateBaseCustom = `
        <div class="klevu-fluid kuPreventDocumentClick">
            <%= helper.render('customBlockTopOuter', scope) %>
            <div id="klevuSearchingArea" class="klevuQuickSearchingArea">
                <div ku-container data-container-id="ku_quick_main_container" data-container-role="main">
                    <%= helper.render('customBlockTop', scope) %>
                    <header ku-container data-container-id="ku_quick_main_header_container" data-container-role="header">
                        <section ku-block data-block-id="ku_quick_main_header_sub_panel"></section>
                        <section ku-block data-block-id="ku_quick_main_header_banner">
                            <%= helper.render('klevuQuickPromotionBanner', scope, data, "top") %>
                        </section>
                        <section ku-block data-block-id="ku_quick_main_header_site_navigation">
                          <% if (data.query.productList) { %>
                            <div class="klevuSuggestionsBlock">
                              <%=helper.render('klevuQuickAutoSuggestions',scope) %>
                              <%= helper.render('klevuQuickPageSuggestions', scope) %>
                              <%= helper.render('klevuQuickCategorySuggestions', scope) %>
                            </div>
                          <% } else { %>
                            <div class="klevuSuggestionsBlock">
                              <%=helper.render('klevuQuickAutoSuggestions',scope) %>
                              <%= helper.render('klevuQuickPageSuggestions', scope) %>
                              <%= helper.render('klevuQuickCategorySuggestions', scope) %>
                            </div>
                          <% } %>
                        </section>
                    </header>

                    <% if (data.showQuickFacetedLayoutCharLimitMessage == true) { %>
                        <% var quickFacetedLayoutMessage = (data.quickFacetedLayoutMinCharsTextValue) ? data.quickFacetedLayoutMinCharsTextValue : ""; %>
                        <% if (quickFacetedLayoutMessage != "") { %>
                            <span class="kuFacetedLayoutMinCharText">
                                <%= helper.translate(quickFacetedLayoutMessage) %>
                            </span>
                        <% } %>
                    <% } else { %>
                        <% if (data.query.productList) { %>
                            <%= helper.render('klevuQuickTemplateResultsHeadingTitle', scope, data, "productList") %>
                            <%= helper.render('klevuQuickTemplateTabResults', scope) %>
                            <%= helper.render('klevuQuickProducts', scope) %>
                            <%= helper.render('klevuQuickOtherContent', scope) %>
                        <% } else { %>
                            <div class="kuSearchPersonalizationsContainer">
                                <%= helper.render('klevuSearchPersonalizations', scope) %>
                            </div>
                        <% } %>
                    <% } %>

                    <footer ku-container data-container-id="ku_quick_main_footer_container" data-container-role="footer">
                        <section ku-block data-block-id="ku_quick_main_footer_sub_panel"></section>
                        <section ku-block data-block-id="ku_quick_main_footer_banner">
                            <%= helper.render('klevuQuickPromotionBanner', scope, data, "bottom") %>
                        </section>
                    </footer>
                    <%= helper.render('customBlockBottom', scope) %>
                </div>
            </div>
        </div>`;
});

// Klevu facet customization for TAA, CE etc to show it as Yes or No instead of True or False
window._klvReady = window._klvReady || [];
window._klvReady.push(function () {
    klevu.beforeActivation("landing, catnav", function (data, scope) {
        scope.kScope.template.setTemplate(klevuLandingTemplateFiltersCustom, "filters", true);
    });

    const klevuLandingTemplateFiltersCustom = `
        <% if(data.query[dataLocal].filters.length > 0 ) { %>
            <div class="kuFilters" role="navigation" data-position="left" aria-label="Product Filters" tabindex="0">
                <h3 class="kuFiltersTitleHeading"><%=helper.translate("Narrow by")%></h3>
                <% helper.each(data.query[dataLocal].filters,function(key,filter){ %>
                    <% if(filter.type == "OPTIONS"){ %>
                        <div class="kuFilterBox klevuFilter <%=(filter.multiselect)?'kuMulticheck':''%>" data-filter="<%=filter.key%>" <% if(filter.multiselect){ %> data-singleselect="false" <% } else { %> data-singleselect="true"<% } %>>
                            <div class="kuFilterHead <%=(filter.isCollapsed) ? 'kuExpand' : 'kuCollapse'%>">
                                <% var filter_label = (filter.label=="klevu_price") ? "price" : filter.label; %>
                                <%=helper.translate(filter_label)%>
                            </div>
                            <div data-optionCount="<%= filter.options.length %>" class="kuFilterNames <%= (filter.options.length <= 5 ) ? 'kuFilterShowAll': '' %> <%=(filter.isCollapsed) ? 'kuFilterCollapse' : ''%>">
                                <ul>
                                    <% helper.each(filter.options,function(key,filterOption){ %>
                                        <li <% if(filterOption.selected ==true) { %> class="kuSelected"<% } %>>
                                            <a
                                                target="_self" 
                                                href="#" 
                                                title="<%=helper.escapeHTML(filterOption.name)%>" 
                                                class="klevuFilterOption<% if(filterOption.selected ==true) { %> klevuFilterOptionActive<% } %>" 
                                                data-value="<%=helper.escapeHTML(filterOption.value)%>"
                                                data-name="<%=helper.escapeHTML(filterOption.name)%>" 
                                            >
                                                <span class="kuFilterIcon"></span>
                                                <span class="kufacet-text">
                                                   <%= (filterOption.name === "1" || filterOption.name === "true") ? "Yes" : (filterOption.name === "0" || filterOption.name === "false" ? "No" : filterOption.name) %>
                                                </span>
                                                <% if(filterOption.selected ==true) { %>
                                                    <span class="kuFilterCancel">X</span>
                                                <% } else { %>
                                                    <span class="kuFilterTotal"><%=filterOption.count%></span>
                                                <% } %>
                                            </a>
                                        </li>
                                        
                                    <%  }); %>
                                </ul>
                                <% if(filter.options.length > 5 ) { %>
                                    <div class="kuShowOpt" tabindex="-1">
                                        <span class="kuFilterDot"></span><span class="kuFilterDot"></span><span class="kuFilterDot"></span>
                                    </div>
                                <% } %>
                            </div>
                        </div>
                    <% } else if(filter.type == "SLIDER")  { %>
                        <div class="kuFilterBox klevuFilter" data-filter="<%=filter.key%>">
                            <div class="kuFilterHead <%=(filter.isCollapsed) ? 'kuExpand' : 'kuCollapse'%>">
                                <% var filter_label = (filter.label=="klevu_price") ? "price" : filter.label; %>
                                <%=helper.translate(filter_label)%>
                            </div>
                            <div class="kuFilterNames sliderFilterNames <%=(filter.isCollapsed) ? 'kuFilterCollapse' : ''%>">                           
                                <div class="kuPriceSlider klevuSliderFilter" data-query = "<%=dataLocal%>">
                                    <div data-querykey = "<%=dataLocal%>" class="noUi-target noUi-ltr noUi-horizontal noUi-background kuSliderFilter kuPriceRangeSliderFilter<%=dataLocal%>"></div>
                                </div>
                            </div>
                        </div>
                    <% } else if (filter.type == "RATING")  { %>
                        <div class="kuFilterBox klevuFilter <%=(filter.multiselect)?'kuMulticheck':''%>" data-filter="<%=filter.key%>" <% if(filter.multiselect){ %> data-singleselect="false" <% } else { %> data-singleselect="true"<% } %>>
                            <div class="kuFilterHead <%=(filter.isCollapsed) ? 'kuExpand' : 'kuCollapse'%>">
                                <%=helper.translate(filter.label)%>
                            </div>
                            <div data-optionCount="<%= filter.options.length %>" class="kuFilterNames <%= (filter.options.length <= 5 ) ? 'kuFilterShowAll': '' %> <%=(filter.isCollapsed) ? 'kuFilterCollapse' : ''%>">
                                <ul>
                                    <% helper.each(filter.options,function(key,filterOption){ %>
                                        <li <% if(filterOption.selected ==true) { %> class="kuSelected"<% } %>>
                                            <a
                                                target="_self" 
                                                href="#" 
                                                title="<%=helper.escapeHTML(filterOption.name)%>" 
                                                class="klevuFilterOption<% if(filterOption.selected ==true) { %> klevuFilterOptionActive<% } %>" 
                                                data-value="<%=helper.escapeHTML(filterOption.value)%>"
                                                data-name="<%=helper.escapeHTML(filterOption.name)%>"
                                            >
                                                <span class="kuFilterIcon"></span>
                                                <span class="kufacet-text">
                                                    <div class="klevuFacetStars">
                                                        <div class="klevuFacetRating" style="width:<%=(20*Number(filterOption.name))%>%;"></div>
                                                    </div>
                                                </span>
                                                <% if(filterOption.selected ==true) { %>
                                                    <span class="kuFilterCancel">X</span>
                                                <% } else { %>
                                                    <span class="kuFilterTotal"><%=filterOption.count%></span>
                                                <% } %>
                                            </a>
                                        </li>
                                        
                                    <%  }); %>
                                </ul>
                            </div>
                        </div>
                    <% } else { %>
                        <!-- Other Facets -->
                    <% } %>
                <% }); %>
            </div>
        <% } %>
    `
})